import {
  GenericTable,
  Modal,
  NewButton,
  NewButtonVariant,
  Spinner,
} from "@visiblethread/react-components";
import Flex, { ALIGN_CLASS, DIRECTION_CLASS, GAP_CLASS } from "components/Flex";
import { modalTypes } from "constants/types";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import {
  exportOpportunityToProposalCsv,
  exportOpportunityToProposalPdf,
  selectOpportunityToProposalData,
  selectOpportunityToProposalError,
  selectOpportunityToProposalLoading,
} from "slices/opportunityToProposal";
import { opportunitiesLabel } from "utils/opportunities";

import { columns } from "./columns";
import styles from "./OpportunityToProposalModal.module.scss";

const MODAL_TITLE = "Opportunity to Proposal";
const EMPTY_MSG = "No tracked opportunities in this period.";

const subtitleLabel = (total, withProposal, withoutProposal) =>
  `${opportunitiesLabel(total)} · ${withProposal} with a proposal, ${withoutProposal} without`;

const OpportunityToProposalModal = () => {
  const dispatch = useDispatch();
  const data = useSelector(selectOpportunityToProposalData);
  const isLoading = useSelector(selectOpportunityToProposalLoading);
  const error = useSelector(selectOpportunityToProposalError);

  const onDismiss = () => {
    dispatch(hideModal(modalTypes.OPPORTUNITY_TO_PROPOSAL));
  };

  const allOpportunities = useMemo(
    () => [
      ...(data?.opportunitiesWithProposal ?? []).map((opp) => ({
        ...opp,
        hasProposal: true,
      })),
      ...(data?.opportunitiesWithoutProposal ?? []).map((opp) => ({
        ...opp,
        hasProposal: false,
      })),
    ],
    [data]
  );

  const totalCount = data?.totalOpportunityCount ?? 0;

  return (
    <Modal
      backdrop
      centered
      className={`fade-in ${styles.modal}`}
      onDismiss={onDismiss}
      show
      size="lg"
    >
      <Modal.Header>{MODAL_TITLE}</Modal.Header>
      <Modal.Body>
        <Flex
          direction={DIRECTION_CLASS.column}
          alignItems={ALIGN_CLASS.stretch}
          gap={GAP_CLASS.GAP_400}
          className={styles.opportunityToProposalModal}
        >
          {error && (
            <div
              className={styles.opportunityToProposalModal__error}
              role="alert"
            >
              {error}
            </div>
          )}
          {!error && (
            <Flex alignItems={ALIGN_CLASS.center} gap={GAP_CLASS.GAP_200}>
              <p className={styles.opportunityToProposalModal__subHeader}>
                {subtitleLabel(
                  totalCount,
                  data?.withProposalCount ?? 0,
                  data?.withoutProposalCount ?? 0
                )}
              </p>
              {isLoading && <Spinner />}
            </Flex>
          )}
          {!error && totalCount === 0 && !isLoading && (
            <p className={styles.opportunityToProposalModal__empty}>
              {EMPTY_MSG}
            </p>
          )}
          {!error && allOpportunities.length > 0 && (
            <div className={styles.opportunityToProposalModal__tableWrapper}>
              <GenericTable columns={columns} data={allOpportunities} />
            </div>
          )}
          {!error && (
            <Flex justifyContent="flex-end" gap={GAP_CLASS.GAP_200}>
              <NewButton
                disabled={totalCount === 0}
                onClick={() => dispatch(exportOpportunityToProposalCsv())}
                variant={NewButtonVariant.l2}
              >
                Export CSV
              </NewButton>
              <NewButton
                disabled={totalCount === 0}
                onClick={() => dispatch(exportOpportunityToProposalPdf())}
                variant={NewButtonVariant.l1}
              >
                Export PDF
              </NewButton>
            </Flex>
          )}
        </Flex>
      </Modal.Body>
    </Modal>
  );
};

export default OpportunityToProposalModal;
